import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, EyeOff } from "lucide-react";
import { adminProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct } from "../../lib/api";

const dark = "w-full bg-transparent border border-[#26241f] px-4 py-3 text-[#f4f2ec] placeholder:text-[#5c584f] focus:border-gold focus:outline-none transition-colors text-sm";
const lbl = "block text-[0.6rem] uppercase tracking-[0.18em] text-[#9a968c] mb-2";

const empty = {
  slug: "", name: "", subtitle: "", description: "", notes: "", category: "scent",
  price: "", compare_price: "", image: "", images: [], active: true, featured: false,
  sort: 0, stock: "", scent_choice_count: 0, billing_interval: "", sku: "",
};

const CATEGORIES = ["kit", "diffuser", "scent", "accessory", "bundle", "subscription"];

const ProductModal = ({ product, token, onClose, onSaved }) => {
  const [form, setForm] = useState(() =>
    product ? { ...product, compare_price: product.compare_price ?? "", stock: product.stock ?? "" } : empty
  );
  const [saving, setSaving] = useState(false);
  const editing = !!product?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.slug || form.price === "") return toast.error("Name, URL slug and price are required.");
    const payload = {
      ...form,
      billing_interval: form.billing_interval || null,
      price: Number(form.price),
      compare_price: form.compare_price === "" ? null : Number(form.compare_price),
      stock: form.stock === "" ? null : Number(form.stock),
      sort: Number(form.sort) || 0,
      scent_choice_count: Number(form.scent_choice_count) || 0,
      images: (form.images || []).filter((x) => x && x.trim()),
    };
    delete payload.id; delete payload.created_at;
    setSaving(true);
    try {
      if (editing) await adminUpdateProduct(product.id, payload, token);
      else await adminCreateProduct(payload, token);
      toast.success(editing ? "Product updated." : "Product created.");
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm grid place-items-start justify-center px-4 overflow-y-auto py-10" data-testid="product-modal">
      <div className="w-full max-w-2xl bg-[#0a0a09] border border-[#26241f] p-8">
        <div className="flex items-center justify-between mb-7">
          <h3 className="font-serif text-2xl text-[#f4f2ec]">{editing ? "Edit product" : "New product"}</h3>
          <button onClick={onClose} className="text-[#9a968c] hover:text-gold" data-testid="product-modal-close"><X size={20} /></button>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><label className={lbl}>Name</label><input className={dark} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="prod-name" /></div>
            <div><label className={lbl}>URL slug</label><input className={dark} value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="prod-slug" /></div>
            <div><label className={lbl}>Price (AUD)</label><input className={dark} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="prod-price" /></div>
            <div><label className={lbl}>Compare-at price (optional)</label><input className={dark} value={form.compare_price} onChange={(e) => set("compare_price", e.target.value)} data-testid="prod-compare" /></div>
            <div>
              <label className={lbl}>Category</label>
              <select className={dark} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="prod-category">
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0a0a09]">{c}</option>)}
              </select>
            </div>
            <div><label className={lbl}>SKU</label><input className={dark} value={form.sku} onChange={(e) => set("sku", e.target.value)} data-testid="prod-sku" /></div>
            <div><label className={lbl}>Stock (blank = unlimited)</label><input className={dark} value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="prod-stock" /></div>
            <div><label className={lbl}>Sort order</label><input className={dark} value={form.sort} onChange={(e) => set("sort", e.target.value)} data-testid="prod-sort" /></div>
          </div>

          <div><label className={lbl}>Short subtitle</label><input className={dark} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="prod-subtitle" /></div>
          <div><label className={lbl}>Fragrance notes (scents only)</label><input className={dark} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="prod-notes" /></div>
          <div><label className={lbl}>Description</label><textarea rows={4} className={`${dark} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="prod-description" /></div>
          <div><label className={lbl}>Main image URL</label><input className={dark} value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="prod-image" /></div>
          <div>
            <label className={lbl}>Gallery image URLs (one per line)</label>
            <textarea rows={3} className={`${dark} resize-none`} value={(form.images || []).join("\n")}
              onChange={(e) => set("images", e.target.value.split("\n"))} data-testid="prod-images" />
          </div>
          <div>
            <label className={lbl}>Bundled fragrance choices (0 = none)</label>
            <input className={dark} value={form.scent_choice_count} onChange={(e) => set("scent_choice_count", e.target.value)} data-testid="prod-scent-count" />
            <p className="text-[0.65rem] text-[#6f6c64] mt-2">Set to 6 on the Car Scent Kit so buyers pick six fragrances.</p>
          </div>

          <div>
            <label className={lbl}>Billing</label>
            <select className={dark} value={form.billing_interval || ""} onChange={(e) => set("billing_interval", e.target.value)} data-testid="prod-billing">
              <option value="" className="bg-[#0a0a09]">One-off purchase</option>
              <option value="month" className="bg-[#0a0a09]">Recurring — monthly subscription</option>
            </select>
            <p className="text-[0.65rem] text-[#6f6c64] mt-2">Monthly products are billed by Stripe until the customer cancels, and ship free.</p>
          </div>

          <div className="flex flex-wrap gap-7 pt-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#c8a24a] w-4 h-4" data-testid="prod-active" />
              <span className="text-sm text-[#cfccc4]">Visible in shop</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#c8a24a] w-4 h-4" data-testid="prod-featured" />
              <span className="text-sm text-[#cfccc4]">Featured</span>
            </label>
          </div>

          <button onClick={save} disabled={saving} data-testid="prod-save"
            className="w-full px-6 py-4 bg-gold text-[#050505] text-xs uppercase tracking-[0.2em] hover:bg-[#e6c56a] transition-colors disabled:opacity-60 mt-2">
            {saving ? "Saving…" : editing ? "Update product" : "Create product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductsTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    adminProducts(token).then((r) => setProducts(r.data)).catch(() => toast.error("Failed to load products."));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await adminDeleteProduct(id, token);
      toast.success("Product deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="products-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <p className="text-sm text-[#9a968c]">Everything on the shop — prices, copy, images and visibility.</p>
        <button onClick={() => setModal({})} data-testid="add-product-button"
          className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-[#050505] text-xs uppercase tracking-[0.15em] hover:bg-[#e6c56a] transition-colors">
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="border border-[#1a1814] overflow-x-auto">
        <table className="w-full text-left" data-testid="products-table">
          <thead>
            <tr className="border-b border-[#1a1814] text-[#6f6c64] text-xs uppercase tracking-[0.15em]">
              <th className="p-5 font-normal">Product</th>
              <th className="p-5 font-normal">Category</th>
              <th className="p-5 font-normal">Price</th>
              <th className="p-5 font-normal">Stock</th>
              <th className="p-5 font-normal">Status</th>
              <th className="p-5 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-[#12110e] hover:bg-[#0a0a09] transition-colors" data-testid={`product-row-${p.slug}`}>
                <td className="p-5">
                  <div className="flex items-center gap-4">
                    {p.image && <img src={p.image} alt="" className="w-12 h-12 object-cover bg-[#12110e]" />}
                    <div>
                      <p className="text-[#f4f2ec]">{p.name}</p>
                      <p className="text-xs text-[#6f6c64]">/{p.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="p-5 text-[#cfccc4] text-sm capitalize">{p.category}</td>
                <td className="p-5 text-gold text-sm">${p.price.toFixed(2)}</td>
                <td className="p-5 text-[#cfccc4] text-sm">{p.stock == null ? "—" : p.stock}</td>
                <td className="p-5">
                  {p.active
                    ? <span className="text-[0.65rem] uppercase tracking-[0.15em] text-gold border border-goldline px-3 py-1.5">Live</span>
                    : <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-[0.15em] text-[#6f6c64] border border-[#26241f] px-3 py-1.5"><EyeOff size={11} /> Hidden</span>}
                </td>
                <td className="p-5">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(p)} className="p-2 text-[#9a968c] hover:text-gold transition-colors" data-testid={`edit-product-${p.slug}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(p.id, p.name)} className="p-2 text-[#9a968c] hover:text-red-400 transition-colors" data-testid={`delete-product-${p.slug}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <ProductModal product={modal.id ? modal : null} token={token} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
};
